import { IMagnoliaContext } from '@/types';
import { LoggerService } from '.';
import { getMagnoliaContext } from './GetMagnoliaContext';

interface CustomWindow extends Window {
  mgnlRefresh?: () => void;
  mgnlFrameReady?: () => void;
}

export interface IEditorContextService {
  isSameOrigin: () => boolean;
  inIframe: () => boolean;
  inPreviewAsVisitor: () => boolean;
  inEditor: () => boolean;
  inEditorAsync: (limit?: number) => Promise<boolean>;
  inEditorPreview: () => boolean;
  isRenderingAnnotation: () => boolean;
  refresh: (delayInMilliseconds?: number) => void;
  isPageEditorReady: () => boolean;
  onFrameReady: () => void;
  setServerUrl: (url: string) => void;
  getMagnoliaContext: (
    requestUrl: string,
    spaRootNodePath?: string,
    languages?: string[]
  ) => IMagnoliaContext;
}

function createService(): IEditorContextService {
  const isSameOrigin = () => {
    try {
      const { href } = window.parent.location;
      return href != null;
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      return false;
    }
  };

  function getWindowInstance(): CustomWindow | undefined {
    if (typeof window === 'undefined') {
      return undefined;
    }
    return isSameOrigin() ? window.parent : window;
  }

  const context = {
    serverUrl:
      isSameOrigin() && typeof window !== 'undefined'
        ? getWindowInstance()?.location.href
        : '',
  };

  const sleep = (ms: number) =>
    new Promise(r => {
      setTimeout(r, ms);
    });
  const waitFor = async (
    f: () => number | boolean,
    startTime?: Date,
    limit = 5000
  ) => {
    while (!f()) {
      await sleep(200);
      if (limit > 0 && startTime && Date.now() - startTime.getTime() > limit) {
        LoggerService.warn('Wait timeout');
        break;
      }
    }
    return f();
  };

  const inIframe = (): boolean => {
    if (typeof window !== 'undefined') {
      return window !== window.parent;
    }
    return false;
  };

  const inPreviewAsVisitor = () => {
    const windowInstance = getWindowInstance();
    return (
      new URLSearchParams(windowInstance?.location.search).get(
        'mgnlPreviewAsVisitor'
      ) === 'true'
    );
  };

  /**
   * @deprecated Since version 1.1.0. Use inEditorAsync instead.
   */
  const inEditor = (): boolean => {
    return !!context.serverUrl?.endsWith(':edit');
  };

  const inEditorAsync = async (limit?: number): Promise<boolean> => {
    await waitFor(() => context.serverUrl?.length || 0 > 0, new Date(), limit);
    return !!context.serverUrl?.endsWith(':edit');
  };

  /**
   * @deprecated Since version 1.1.0. Use inEditorAsync instead.
   */
  const inEditorPreview = (): boolean => {
    return !!context.serverUrl?.endsWith(':view');
  };

  /**
   * @deprecated Since version 1.1.0. Use inEditorAsync instead.
   */
  const isRenderingAnnotation = () => {
    return inEditor() || !context.serverUrl;
  };

  let refreshTimeoutID: NodeJS.Timeout | null = null;
  const refresh = (delayInMilliseconds?: number) => {
    if (refreshTimeoutID) {
      clearTimeout(refreshTimeoutID);
    }
    refreshTimeoutID = setTimeout(callMgnlRefresh, delayInMilliseconds);
  };

  const callMgnlRefresh = () => {
    const windowInstance = getWindowInstance();
    if (windowInstance?.mgnlRefresh) {
      if (
        document.documentElement.innerHTML.indexOf('cms:page content=') > -1
      ) {
        windowInstance.mgnlRefresh();
      } else {
        refresh(200);
      }
    }
  };

  const isPageEditorReady = () => {
    const windowInstance = getWindowInstance();
    return windowInstance?.mgnlFrameReady !== undefined;
  };

  const onFrameReady = () => {
    const windowInstance = getWindowInstance();
    if (windowInstance?.mgnlFrameReady) {
      windowInstance.mgnlFrameReady();
    }
  };

  const setServerUrl = (url: string) => {
    context.serverUrl = url;
  };
  return {
    isSameOrigin,
    inIframe,
    inEditorAsync,
    inEditorPreview,
    inPreviewAsVisitor,
    isRenderingAnnotation,
    isPageEditorReady,
    onFrameReady,
    setServerUrl,
    inEditor,
    refresh,
    getMagnoliaContext,
  };
}

export const EditorContextService = createService();
